import { useState, useEffect } from 'react'
import { SwitchVerticalIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid'
import classnames from 'classnames'
import { renderColumn } from './table-utils'
import { useFetchData } from '@utils/hooks'
import { useTranslation } from '@utils/contexts';

const limit = [10, 25, 50, 100]

export default function DynamicTable({ columns,
  path,
  defaultQueries = { page: 1, limit: 10, order: '', searchStr: '' },
  CustomHeader = null }
) {
  const [results, setResults] = useState(null)
  const [orderObj, setOrderObj] = useState({})
  const [queryObj, setQueryObj] = useState({ ...defaultQueries })

  const [data, fetchData] = useFetchData({ path, queryString: queryObj })
  const { t } = useTranslation();

  useEffect(() => {
    if (data) fetchData()
  }, [path, queryObj])

  useEffect(() => {
    setQueryObj(defaultQueries)
  }, [defaultQueries])

  useEffect(() => {
    setResults(data)
  }, [data])


  const onSearch = (e) => {
    setQueryObj(prev => ({ ...prev, searchStr: e.target.value }))

  }

  const onSort = (i) => {
    const newOrder = { ...orderObj }
    if (newOrder[i.key]) {
      newOrder[i.key] = (newOrder[i.key] === 'desc') ? 'asc' : 'desc'
    } else {
      newOrder[i.key] = 'asc'
    }
    setOrderObj(newOrder)
    setQueryObj(prev => ({ ...prev, order: `${i.key}_${newOrder[i.key]}` }))

  }

  return (

    <div className="w-full">
      <div className="flex flex-col justify-between my-2 md:flex-row">
        <div className="flex items-center justify-end w-full py-3 md:justify-start md:py-0">
          {CustomHeader && <CustomHeader />}
        </div>
        <div className="w-full flex-0 md:w-96 ">
          <div className="relative mt-1 rounded-md ">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <SearchIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="text"
              onChange={onSearch}
              className="block w-full pl-10 border-gray-300 rounded-md shadow-sm focus:ring-0 focus:border-gray-300 sm:text-sm"
              placeholder={t('component.table.search')}
            />
          </div>
        </div>
      </div>
      {
        !!(results?.data?.length) ?
          <div className="overflow-y-auto">
            <table className="w-full overflow-hidden shadow whitespace-nowrap sm:rounded-md">
              <thead>
                <tr tabIndex="0" className="w-full h-16 text-sm leading-none text-gray-800 focus:outline-none">
                  {
                    columns.map((c, idx) => (
                      <th key={`h_${idx}`} className={`font-normal text-left pl-2 capitalize ${c.width ? c.width : ''}`}>
                        {(c.key && (
                          <p className={classnames("flex items-center", { 'cursor-pointer': c.sort })} onClick={() => onSort(c)}>
                            {c.label || c.key}
                            {c.sort && (
                              <SwitchVerticalIcon className="w-4 h-4 ml-3 text-gray-400" aria-hidden="true" />
                            )}
                          </p>
                        ))}

                      </th>
                    ))
                  }
                </tr>
              </thead>
              {/* BODY */}
              <tbody className="w-full">
                {
                  results && (
                    results.data.map((d, idx) => (
                      <tr key={`b_${idx}`} tabIndex="0" className="h-24 text-sm leading-none text-gray-800 bg-white border-t border-b border-gray-100 focus:outline-none hover:bg-gray-50 group">
                        {renderColumn(d, columns)}
                      </tr>
                    ))
                  )
                }</tbody>
            </table>
            {results && (
              <div className="flex justify-end mt-2">
                <Pagination
                  items={results.total}
                  config={{ ...queryObj }}
                  onPageChange={(page) => setQueryObj(prev => ({ ...prev, page }))}
                  onShowPageChange={(limit) => setQueryObj(prev => ({ ...prev, limit }))}
                />
              </div>
            )}
          </div>
          :
          <div className="flex items-center justify-center w-full h-20">
            <p className="mt-2 text-xl text-gray-500">{t('component.table.no-data')}</p>
          </div>
      }

    </div>
  )
}


const Pagination = ({ items, config, onPageChange, onShowPageChange }) => {
  const { t } = useTranslation();

  const maxNumber = Math.ceil(items / config.limit)
  const renderNumber = (c, m) => {
    var current = c,
      last = m,
      delta = 2,
      left = current - delta,
      right = current + delta + 1,
      range = [],
      rangeWithDots = [],
      l;

    for (let i = 1; i <= last; i++) {
      if (i == 1 || i == last || i >= left && i < right) {
        range.push(i);
      }
    }

    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  }

  return (
    <div className="flex items-center justify-between w-full py-3">
      <div className="flex justify-between flex-1 sm:hidden">
        <button
          onClick={() => {
            if (config.page > 1) onPageChange(config.page - 1)
          }}
          className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          {t('component.table.Previous')}
        </button>
        <button
          onClick={() => {
            if (maxNumber > config.page) onPageChange(config.page + 1)
          }}
          className="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          {t('component.table.Next')}
        </button>
      </div>
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div className="space-y-1">
          <p className="text-sm text-gray-700">
            {t('component.table.Showing')} <span className="font-medium">{(config.page * config.limit) - (config.limit - 1)}</span> {t('component.table.to')} <span className="font-medium">{config.page * config.limit}</span> {t('component.table.of')}{' '}
            <span className="font-medium">{items}</span> {t('component.table.results')}
          </p>
          <div className="flex space-x-3">
            <p className="text-sm text-gray-700">{t('component.table.Showing')}</p>
            <select
              className="block w-auto px-6 py-0 bg-transparent border-none rounded-md focus:outline-none focus:ring-0 sm:text-sm"
              defaultValue={config.limit}
              onChange={(event) => onShowPageChange(event.target.value)}

            >

              {limit.map((i, idx) => <option key={i} value={i}>{i}</option>)}
            </select>
            <p className="text-sm text-gray-700">{t('component.table.per page')} </p>
          </div>
        </div>
        <div>
          <nav className="relative z-0 inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
            <a
              href="#"
              onClick={() => {
                if (config.page > 1) onPageChange(config.page - 1)
              }}
              className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50"
            >
              <span className="sr-only">{t('component.table.Previous')}</span>
              <ChevronLeftIcon className="w-5 h-5" aria-hidden="true" />
            </a>
            {
              renderNumber(config.page, maxNumber).map((i, idx) => (
                i === '...' ?
                  <span key={`page_dot_${idx}`} className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300">
                    ...
                  </span>
                  :
                  <a
                    key={`page_${i}`}
                    onClick={() => onPageChange(i)}
                    aria-current="page"
                    className={classnames('', {
                      'z-10 bg-primary-50 border-primary-200 text-primary-500 relative inline-flex items-center px-4 py-2 border text-sm font-medium': i === config.page,
                      'cursor-pointer  bg-white border-gray-300 text-gray-500 hover:bg-gray-50 relative inline-flex items-center px-4 py-2 border text-sm font-medium': i !== config.page,

                    })}
                  >
                    {i}
                  </a>
              ))
            }
            <a
              onClick={() => {
                if (maxNumber > config.page) onPageChange(config.page + 1)
              }}
              href="#"
              className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50"
            >
              <span className="sr-only" >{t('component.table.Next')}</span>
              <ChevronRightIcon className="w-5 h-5" aria-hidden="true" />
            </a>
          </nav>
        </div>
      </div>
    </div>
  )
}